import type Hls from 'hls.js';
import { onBeforeUnmount } from 'vue';
import { createCachingLoader } from '@/utils/hls-video-cache';

const instances = new WeakMap<HTMLVideoElement, Hls>();

let hlsEvents: typeof import('hls.js').default.Events | null = null;

const PLAYBACK_END_TOLERANCE_SEC = 0.35;
const PLAYBACK_END_POLL_MS = 500;
const STALL_CHECK_DELAY_MS = 8_000;

function isPlaybackNearEnd(videoEl: HTMLVideoElement): boolean {
  if (videoEl.ended) return true;

  const { duration, currentTime } = videoEl;
  if (Number.isFinite(duration) && duration > 0) {
    return currentTime >= duration - PLAYBACK_END_TOLERANCE_SEC;
  }

  return false;
}

/**
 * HLS on older Chromium often never fires `ended` and reports `duration` as Infinity.
 * Combine native signals, hls.js MEDIA_ENDED, and a poll fallback.
 */
export function attachPlaybackEndDetection(
  videoEl: HTMLVideoElement,
  onEnd: () => void,
): () => void {
  const cleanups: Array<() => void> = [];
  let fired = false;

  const fire = () => {
    if (fired) return;
    fired = true;
    cleanup();
    onEnd();
  };

  const cleanup = () => {
    for (const dispose of cleanups) dispose();
    cleanups.length = 0;
  };

  const onEnded = () => fire();
  videoEl.addEventListener('ended', onEnded);
  cleanups.push(() => videoEl.removeEventListener('ended', onEnded));

  const onTimeUpdate = () => {
    if (isPlaybackNearEnd(videoEl)) fire();
  };
  videoEl.addEventListener('timeupdate', onTimeUpdate);
  cleanups.push(() => videoEl.removeEventListener('timeupdate', onTimeUpdate));

  const onPause = () => {
    if (isPlaybackNearEnd(videoEl)) fire();
  };
  videoEl.addEventListener('pause', onPause);
  cleanups.push(() => videoEl.removeEventListener('pause', onPause));

  const hls = instances.get(videoEl);
  const mediaEndedEvent = hlsEvents?.MEDIA_ENDED;
  if (hls && mediaEndedEvent) {
    const onMediaEnded = () => fire();
    hls.on(mediaEndedEvent, onMediaEnded);
    cleanups.push(() => hls.off(mediaEndedEvent, onMediaEnded));
  }

  const pollId = window.setInterval(() => {
    if (videoEl.ended || isPlaybackNearEnd(videoEl)) fire();
  }, PLAYBACK_END_POLL_MS);
  cleanups.push(() => window.clearInterval(pollId));

  return cleanup;
}

type LoadHlsVideoOptions = {
  /** Serve the highest quality variant from the IndexedDB video cache, filling it as it streams. */
  cached?: boolean;
};

export async function loadHlsVideo(
  videoEl: HTMLVideoElement,
  fileUrl: string,
  { cached = false }: LoadHlsVideoOptions = {},
): Promise<void> {
  destroyHlsVideo(videoEl);

  const HlsModule = (await import('hls.js')).default;
  hlsEvents = HlsModule.Events;
  if (HlsModule.isSupported()) {
    const hls = new HlsModule(cached ? { loader: createCachingLoader(HlsModule) } : {});
    hls.loadSource(fileUrl);
    hls.attachMedia(videoEl);
    instances.set(videoEl, hls);
    return;
  }

  videoEl.src = fileUrl;
}

export function destroyHlsVideo(videoEl: HTMLVideoElement | null) {
  if (!videoEl) return;
  const hls = instances.get(videoEl);
  if (hls) {
    hls.destroy();
    instances.delete(videoEl);
  }
  videoEl.removeAttribute('src');
  videoEl.load();
}

/** Muted autoplay for signage / kiosk contexts where user gesture is never available. */
export async function playMutedVideo(videoEl: HTMLVideoElement): Promise<boolean> {
  videoEl.muted = true;
  try {
    await videoEl.play();
  } catch {
    return false;
  }

  return !videoEl.paused;
}

export function waitForRenderableVideo(
  videoEl: HTMLVideoElement,
  timeoutMs = STALL_CHECK_DELAY_MS,
): Promise<void> {
  if (videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(
        new Error(
          `Video stall: no rendered frames (videoWidth=${videoEl.videoWidth}, readyState=${videoEl.readyState}, paused=${videoEl.paused})`,
        ),
      );
    }, timeoutMs);

    const onData = () => {
      if (videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
        cleanup();
        resolve();
      }
    };
    const onError = () => {
      cleanup();
      reject(new Error(`Video error ${videoEl.error?.code ?? 'unknown'} during stall check`));
    };
    const cleanup = () => {
      window.clearTimeout(timeoutId);
      videoEl.removeEventListener('loadeddata', onData);
      videoEl.removeEventListener('resize', onData);
      videoEl.removeEventListener('playing', onData);
      videoEl.removeEventListener('error', onError);
    };

    videoEl.addEventListener('loadeddata', onData);
    videoEl.addEventListener('resize', onData);
    videoEl.addEventListener('playing', onData);
    videoEl.addEventListener('error', onError, { once: true });
    onData();
  });
}

export function useHlsVideoCleanup(videoEls: () => (HTMLVideoElement | null)[]) {
  onBeforeUnmount(() => {
    for (const el of videoEls()) {
      destroyHlsVideo(el);
    }
  });
}
